'use client';
import { LineChart, Line, YAxis, ResponsiveContainer, ReferenceDot, ReferenceLine } from 'recharts';
import { useResource } from '@/lib/use-resource';
import type { ActionAlert } from '@/lib/action-types';

interface PricePoint { date: string; close: number; }
interface PriceResponse { symbol: string; prices: PricePoint[]; }

export function AlertEvidence({ alert }: { alert: ActionAlert }) {
  const d = alert.detail;
  const cat = alert.category;

  if (cat === 'transcript') {
    return <TranscriptEvidence symbol={alert.symbol} />;
  }

  if (cat === 'drawdown') {
    return <DrawdownEvidence symbol={alert.symbol} />;
  }

  if (cat === 'revision-velocity') {
    const d7 = d.revisionDelta7d as number | undefined;
    const d30 = d.revisionDelta30d as number | undefined;
    const accel = d.accelValue as number | undefined;
    const epsNow = d.eps1yNow as number | undefined;
    const epsThen = d.eps1yWeekAgo as number | undefined;
    const pct = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
    return (
      <div className="mt-1.5 text-[10px] font-mono space-y-0.5">
        {d7 != null && d30 != null && (
          <div className="text-[#64748b]">
            {'7d estimate '}<span className={d7 < 0 ? 'text-red-300' : 'text-emerald-300'}>{pct(d7)}</span>
            {'  ·  30d estimate '}<span className={d30 < 0 ? 'text-red-300' : 'text-emerald-300'}>{pct(d30)}</span>
            {accel != null && (
              <span className="text-[#475569]">{'  ·  accel '}{accel >= 0 ? '+' : ''}{accel.toFixed(3)}</span>
            )}
          </div>
        )}
        {epsNow != null && epsThen != null && (
          <div className="text-[#475569]">
            {'FY+1 EPS consensus '}
            <span className="text-[#94a3b8]">${epsThen.toFixed(2)}</span>
            {' → '}
            <span className="text-[#e2e8f0]">${epsNow.toFixed(2)}</span>
          </div>
        )}
      </div>
    );
  }

  if (cat === 'estimate-revision') {
    const now = d.valueNow as number | undefined;
    const then = d.valueThen as number | undefined;
    if (now != null && then != null) {
      const chg = now - then;
      const pct = then !== 0 ? (chg / Math.abs(then)) * 100 : 0;
      const neg = chg < 0;
      return (
        <div className="mt-1.5 text-[10px] font-mono text-[#64748b]">
          FY+1 EPS{' '}
          <span className="text-[#94a3b8]">${then.toFixed(2)}</span>
          {' → '}
          <span className="text-[#e2e8f0]">${now.toFixed(2)}</span>
          {'  '}
          <span className={neg ? 'text-red-300' : 'text-emerald-300'}>
            {neg ? '' : '+'}{chg.toFixed(2)} ({neg ? '' : '+'}{pct.toFixed(1)}%)
          </span>
        </div>
      );
    }
  }

  if (cat === 'target-price') {
    const now = d.valueNow as number | undefined;
    const then = d.valueThen as number | undefined;
    if (now != null && then != null) {
      const chg = now - then;
      const pct = then !== 0 ? (chg / Math.abs(then)) * 100 : 0;
      const neg = chg < 0;
      return (
        <div className="mt-1.5 text-[10px] font-mono text-[#64748b]">
          Analyst target{' '}
          <span className="text-[#94a3b8]">${then.toFixed(0)}</span>
          {' → '}
          <span className="text-[#e2e8f0]">${now.toFixed(0)}</span>
          {'  '}
          <span className={neg ? 'text-red-300' : 'text-emerald-300'}>
            {neg ? '' : '+'}{chg.toFixed(0)} ({neg ? '' : '+'}{pct.toFixed(1)}%)
          </span>
        </div>
      );
    }
  }

  if (cat === 'insider') {
    const val = d.signalValue as number | undefined;
    if (val != null) {
      const neg = val < 0;
      const abs = Math.abs(val);
      const fmt = abs >= 1_000_000
        ? `$${(abs / 1_000_000).toFixed(1)}M`
        : `$${(abs / 1_000).toFixed(0)}K`;
      return (
        <div className="mt-1.5 text-[10px] font-mono text-[#64748b]">
          Net insider{' '}
          <span className={neg ? 'text-red-300' : 'text-emerald-300'}>
            {neg ? 'sales' : 'buys'} {fmt}
          </span>
          {' past 7d'}
        </div>
      );
    }
  }

  if (cat === 'price-run' || cat === 'price-drop') {
    const priceThen = d.priceThen as number | undefined;
    const priceNow = d.priceNow as number | undefined;
    const pctVal = d.priceDeltaPct as number | undefined;
    const win = String(d.window ?? '5d');
    if (priceThen != null && priceNow != null) {
      const neg = (priceNow - priceThen) < 0;
      return (
        <div className="mt-1.5 text-[10px] font-mono text-[#64748b]">
          <span className={neg ? 'text-red-300' : 'text-emerald-300'}>
            {neg ? 'Down' : 'Up'} {pctVal != null ? Math.abs(pctVal * 100).toFixed(1) : '—'}%
          </span>
          {` in ${win}  `}
          <span className="text-[#94a3b8]">${priceThen.toFixed(2)}</span>
          {' → '}
          <span className="text-[#e2e8f0]">${priceNow.toFixed(2)}</span>
        </div>
      );
    }
  }

  if (cat === 'composite' || cat === 'v-score' || cat === 'q-score' || cat === 'factor') {
    const now = d.valueNow as number | undefined;
    const then = d.valueThen as number | undefined;
    const win = String(d.window ?? '');
    const rawMetric = String(d.metric ?? '');
    const label = rawMetric.includes(':') ? rawMetric.split(':').slice(1).join(' ') : rawMetric;
    if (now != null && then != null) {
      const delta = now - then;
      const neg = delta < 0;
      return (
        <div className="mt-1.5 text-[10px] font-mono text-[#64748b]">
          {label}{' '}
          <span className="text-[#94a3b8]">{then.toFixed(2)}</span>
          {' → '}
          <span className="text-[#e2e8f0]">{now.toFixed(2)}</span>
          {win && ` over ${win}`}
          {'  '}
          <span className={neg ? 'text-red-300' : 'text-emerald-300'}>
            {neg ? '' : '+'}{delta.toFixed(2)}
          </span>
        </div>
      );
    }
  }

  if (cat === 'dividend') {
    const now = d.valueNow as number | undefined;
    const then = d.valueThen as number | undefined;
    if (now != null && then != null) {
      const chg = now - then;
      const neg = chg < 0;
      return (
        <div className="mt-1.5 text-[10px] font-mono text-[#64748b]">
          Indicated yield{' '}
          <span className="text-[#94a3b8]">{(then * 100).toFixed(2)}%</span>
          {' → '}
          <span className="text-[#e2e8f0]">{(now * 100).toFixed(2)}%</span>
          {'  '}
          <span className={neg ? 'text-red-300' : 'text-emerald-300'}>
            {neg ? 'cut' : 'hike'} {Math.abs(chg * 100).toFixed(2)}pp
          </span>
        </div>
      );
    }
  }

  return null;
}

function DrawdownEvidence({ symbol }: { symbol: string }) {
  const { data } = useResource<PriceResponse>(`/api/prices?symbol=${symbol}`, 5 * 60_000);
  const prices = data?.prices;
  if (!prices || prices.length < 30) {
    return <div className="mt-1.5 text-[10px] text-[#64748b] italic">Loading price history…</div>;
  }
  const last252 = prices.slice(-252);
  let peakPoint = last252[0];
  for (const p of last252) if (p.close > peakPoint.close) peakPoint = p;
  const curPoint = last252[last252.length - 1];
  const drawdownPct = ((curPoint.close - peakPoint.close) / peakPoint.close) * 100;
  return (
    <div className="mt-2">
      <div className="flex items-baseline gap-2 flex-wrap mb-1 text-[10px] font-mono">
        <span className="text-[#64748b]">52w peak <span className="text-[#e2e8f0]">${peakPoint.close.toFixed(2)}</span> on {peakPoint.date}</span>
        <span className="text-[#475569]">·</span>
        <span className="text-[#64748b]">now <span className="text-[#e2e8f0]">${curPoint.close.toFixed(2)}</span></span>
        <span className="text-[#475569]">·</span>
        <span className="text-red-300 font-semibold">{drawdownPct.toFixed(1)}%</span>
      </div>
      <div style={{ width: '100%', height: 80 }}>
        <ResponsiveContainer>
          <LineChart data={last252} margin={{ top: 4, right: 6, bottom: 0, left: 0 }}>
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <ReferenceLine y={peakPoint.close} stroke="#fbbf24" strokeDasharray="2 2" strokeOpacity={0.4} />
            <ReferenceDot x={peakPoint.date} y={peakPoint.close} r={3} fill="#fcd34d" stroke="#fbbf24" />
            <Line type="monotone" dataKey="close" stroke="#94a3b8" strokeWidth={1.2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface StockStoryTranscript {
  period?: string;
  callDate?: string;
  headline?: string;
  oneLiner?: string;
  tone?: 'positive' | 'negative' | 'neutral' | string;
  keyQuotes?: Array<{ speaker?: string; quote?: string; context?: string }>;
}

function TranscriptEvidence({ symbol }: { symbol: string }) {
  const { data } = useResource<{ latestTranscript?: StockStoryTranscript }>(`/api/stock-story/${symbol}`, 60_000);
  const t = data?.latestTranscript;
  if (!t || !t.headline) {
    return <div className="mt-1.5 text-[10px] text-[#64748b] italic">Loading earnings-call summary…</div>;
  }
  const toneClass = t.tone === 'positive' ? 'text-emerald-300'
    : t.tone === 'negative' ? 'text-red-300'
    : 'text-slate-300';
  const toneChip = t.tone === 'positive' ? 'bg-emerald-500/15 border-emerald-500/30'
    : t.tone === 'negative' ? 'bg-red-500/15 border-red-500/30'
    : 'bg-slate-500/15 border-slate-500/30';
  const quote = t.keyQuotes?.[0];
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${toneChip} ${toneClass}`}>
          {t.tone ?? 'neutral'}
        </span>
        {t.period && <span className="text-[9px] text-[#64748b] font-mono">{t.period}</span>}
        {t.callDate && <span className="text-[9px] text-[#64748b] font-mono">· {t.callDate}</span>}
      </div>
      <div className={`text-[11px] font-semibold leading-snug ${toneClass}`}>{t.headline}</div>
      {t.oneLiner && (
        <div className="text-[11px] text-[#94a3b8] leading-snug">{t.oneLiner}</div>
      )}
      {quote?.quote && (
        <div className="mt-1.5 pl-2 border-l-2 border-[#1e293b] text-[10px] text-[#94a3b8] italic leading-snug">
          &ldquo;{quote.quote}&rdquo;
          {quote.speaker && (
            <span className="not-italic text-[#64748b]"> — {quote.speaker}</span>
          )}
        </div>
      )}
    </div>
  );
}
